import {
	WebAPICBSPeriodType,
	WebAPIDataEntryList,
} from '@/globals/webApiTypes/elements/WebAPICBS';
import { ValuesByDimension } from '../lineGraph.types';
import { assertNever } from '@/globals/helpers/assert.helpers';

export const getAllPeriodDates = (dataEntryList: WebAPIDataEntryList) => {
	return dataEntryList
		.reduce((acc, dataEntry) => {
			const { periodDate } = dataEntry;
			if (acc.find((item) => item.getTime() === periodDate.getTime()))
				return acc;
			acc.push(new Date(periodDate));
			return acc;
		}, [] as Array<Date>)
		.toSorted((a, b) => a.getTime() - b.getTime());
};

const hasValueForPeriod =
	({
		periodDate,
		valuesByDimension,
	}: {
		periodDate: Date;
		valuesByDimension: ValuesByDimension;
	}) =>
	(dimensionKey: string) => {
		return valuesByDimension[dimensionKey]?.[periodDate.toString()] != null;
	};

// Returns the date that comes one period (month, quarter, year) after current date
const getIncrementedDate = (
	currentDate: Date,
	periodType: WebAPICBSPeriodType,
) => {
	switch (periodType) {
		case 'Maanden':
			return new Date(currentDate.setUTCMonth(currentDate.getUTCMonth() + 1));
		case 'Kwartalen':
			return new Date(currentDate.setUTCMonth(currentDate.getUTCMonth() + 3));
		case 'Jaar':
			return new Date(
				currentDate.setUTCFullYear(currentDate.getUTCFullYear() + 1),
			);
		default:
			return assertNever(periodType, `Invalid periodType: ${periodType}`);
	}
};

export const getPeriodDatesInRange = ({
	allPeriodDates,
	periodType,
	dimensionKeys,
	valuesByDimension,
}: {
	allPeriodDates: Array<Date>;
	periodType: WebAPICBSPeriodType;
	dimensionKeys: Array<string>;
	valuesByDimension: ValuesByDimension;
}) => {
	const startDate = allPeriodDates.find((periodDate) =>
		dimensionKeys.some(hasValueForPeriod({ periodDate, valuesByDimension })),
	);
	if (!startDate) {
		return [];
	}

	const endDate = allPeriodDates.findLast((periodDate) =>
		dimensionKeys.some(hasValueForPeriod({ periodDate, valuesByDimension })),
	);

	if (!endDate) {
		return [];
	}
	const periodDatesInRange = [];
	let date = startDate;

	while (date.getTime() <= endDate.getTime()) {
		periodDatesInRange.push(new Date(date));
		date = getIncrementedDate(date, periodType);
	}

	return periodDatesInRange;
};
